import { motion } from "framer-motion";
import SectionLabel from "./SectionLabel";

const FRAGMENTS = [
  { id: "01", title: "Design here.", note: "One studio." },
  { id: "02", title: "Fabrication elsewhere.", note: "A different vendor." },
  { id: "03", title: "Digital somewhere else.", note: "A third team." },
];

const PRINCIPLES = [
  {
    n: "01",
    title: "One team defines the brand.",
    body: "Strategy, voice and identity are written by the same hands that will build them.",
  },
  {
    n: "02",
    title: "The same team designs every touchpoint.",
    body: "Visiting card, exhibition booth, packaging, app - all designed inside the same system.",
  },
  {
    n: "03",
    title: "The same system executes everywhere.",
    body: "Across physical and digital environments. No fragmentation. No misalignment.",
  },
];

const Manifesto = () => {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative py-24 md:py-32 border-t-2 border-black"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionLabel className="mb-6" testid="label-manifesto">
              [ 01 ] - POSITIONING
            </SectionLabel>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
              viewport={{ once: true, margin: "-100px" }}
              className="font-archivo font-black h-tighter text-4xl md:text-5xl lg:text-6xl text-black"
              style={{ fontFamily: "Archivo, sans-serif" }}
            >
              We are not a service provider.
            </motion.h2>
          </div>

          <div className="lg:col-span-8 space-y-12">
            {/* The fragmented reality */}
            <div>
              <div className="mono-label mb-6">THE FRAGMENTED REALITY</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/30">
                {FRAGMENTS.map((f, i) => (
                  <motion.div
                    key={f.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-[#CCD6DC] p-7 md:p-8"
                  >
                    <div className="mono-label mb-4 text-black/60">{`/ 0${i + 1}`}</div>
                    <div
                      className="text-black text-xl md:text-2xl line-through decoration-black/60 decoration-[1px]"
                      style={{ fontFamily: "Archivo, sans-serif", letterSpacing: "-0.01em" }}
                    >
                      {f.title}
                    </div>
                    <div className="mono-label mt-4">{f.note}</div>
                  </motion.div>
                ))}
              </div>
              <p
                className="mt-6 text-black/70 max-w-2xl"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                That creates gaps. Things drift. Brands look like committees.
              </p>
            </div>

            {/* UPRCSE works differently */}
            <div>
              <div className="mono-label mb-6 text-[#AE519F]">UPRCSE WORKS DIFFERENTLY</div>
              <div className="space-y-px bg-black/30">
                {PRINCIPLES.map((p, i) => (
                  <motion.div
                    key={p.n}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    viewport={{ once: true }}
                    className="bg-[#CCD6DC] p-7 md:p-8 grid grid-cols-12 gap-6 items-start group hover:bg-[#FFBBFF]/40 transition-colors"
                  >
                    <div className="col-span-2 md:col-span-1 mono-label pt-1">
                      {p.n}
                    </div>
                    <div className="col-span-10 md:col-span-7">
                      <div
                        className="text-black text-2xl md:text-3xl group-hover:text-[#AE519F] transition-colors"
                        style={{
                          fontFamily: "Archivo, sans-serif",
                          letterSpacing: "-0.02em",
                          lineHeight: 1.05,
                        }}
                      >
                        {p.title}
                      </div>
                    </div>
                    <div
                      className="col-span-12 md:col-span-4 text-black/65 text-base"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {p.body}
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 flex items-center gap-3">
                <div className="h-px w-12 bg-black" />
                <div className="mono-label text-[#AE519F]">
                  NO FRAGMENTATION. NO MISALIGNMENT.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
